<template>
  <div>
    <!-- Invisible helper component; only modal renders when needed -->
    <div v-if="showModal" class="vat-guard-modal-overlay" @click.self="onCancel">
      <div class="vat-guard-modal">
        <div class="vat-guard-modal__body">
          <slot name="message">
            <p>{{ modalMessage }}</p>
          </slot>
        </div>
        <div class="vat-guard-modal__actions">
          <button type="button" class="vat-guard-btn vat-guard-btn--secondary" @click="onCancel">{{ cancelText }}</button>
          <button type="button" class="vat-guard-btn btn-success vat-guard-btn--primary" @click="onContinue">{{ continueText }}</button>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
export default {
  name: 'VatCheckGuardV1',
  props: {
    endpointUrl: { type: String, required: true },
    formSelector: { type: String, default: null },
    inputNames: { type: Array, required: true },
    countryIdNames: { type: Array, required: true },
    method: { type: String, default: 'POST' },
    continueText: { type: String, default: 'Continue' },
    cancelText: { type: String, default: 'Cancel' },
    // Optional: customize messages
    errorMessage: { type: String, default: 'VAT number could not be verified. Do you want to continue anyway?' },
  },
  data() {
    return {
      showModal: false,
      modalMessage: '',
      _pendingSubmitEvent: null,
      _pendingProceedCallback: null,
      _resolveGuardPromise: null,
    };
  },
  mounted() {
  },
  methods: {
    _getCsrfToken() {
      let token = null;
      const meta = document.querySelector('meta[name="csrf-token"]');
      if (meta && meta.getAttribute) {
        token = meta.getAttribute('content');
      }
      if (!token && typeof window !== 'undefined') {
        token = (window.Laravel && window.Laravel.csrfToken) || window.csrfToken || null;
      }
      return token || null;
    },
    onCancel() {
      this.showModal = false;
      this._pendingSubmitEvent = null;
      if (typeof this._resolveGuardPromise === 'function') {
        // Inform programmatic callers that user canceled
        this._resolveGuardPromise(false);
        this._resolveGuardPromise = null;
      }
    },
    onContinue() {
      this.showModal = false;
      const form = this.formSelector ? document.querySelector(this.formSelector) : (this.$el.closest && this.$el.closest('form'));

      // If a programmatic callback was provided, run it; otherwise submit the form
      const cb = this._pendingProceedCallback;
      this._pendingProceedCallback = null;

      const finalize = (proceeded) => {
        if (typeof this._resolveGuardPromise === 'function') {
          this._resolveGuardPromise(!!proceeded);
          this._resolveGuardPromise = null;
        }
      };

      if (typeof cb === 'function') {
        Promise.resolve()
          .then(() => cb())
          .then(() => finalize(true))
          .catch(() => finalize(true));
      } else {
        if (form) this._submitForm(form);
        finalize(true);
      }
      this._pendingSubmitEvent = null;
    },
    /**
     * Programmatic VAT check entry-point for @click workflows.
     * Usage from parent: await this.$refs.vatGuard.checkVatAndProceed(() => { /* your submit/save logic */ /* })
     * Resolves to true if proceeded (valid or user pressed Continue), false if user canceled.
     */
    async checkVatAndProceed(callback) {
      // Find target form (if any) to read the VAT value from the input
      let form = null;
      this.modalMessage = '';
      if (this.formSelector) {
        form = document.querySelector(this.formSelector);
      } else {
        form = (this.$el.closest && this.$el.closest('form')) || document.querySelector('form');
      }

      if (!this.inputNames.length && typeof callback === 'function') {
          await callback();
          return true;
      }

      let vatInput, vat, countryIdInput, countryId, payload, json, previousVat, previousCountryId;
      const csrf = this._getCsrfToken();
      for (let i = 0; i < this.inputNames.length; i++) {
          vatInput = form ? form.querySelector(`[name="${this.inputNames[i]}"]`) : null;
          vat = vatInput ? (vatInput.value || '').trim() : '';
          countryIdInput = form.querySelector(`[name="${this.countryIdNames[i]}"]`);
          countryId = countryIdInput ? countryIdInput.value : 1;

          if (previousVat === vat) {
              continue;
          }

          try {
              payload = new URLSearchParams();
              payload.set('vat', vat);
              payload.set('country_id', countryId);

              const resp = await fetch(this.endpointUrl, {
                  method: this.method || 'POST',
                  headers: {
                      'Accept': 'application/json',
                      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                      'X-Requested-With': 'XMLHttpRequest',
                      ...(csrf ? { 'X-CSRF-TOKEN': csrf } : {}),
                  },
                  body: (this.method || 'POST').toUpperCase() === 'GET' ? undefined : payload,
              });

              json = {};
              try { json = await resp.json(); } catch (_) {}

              if (json && json.success === true) {
                  previousVat = vat;
                  continue;
              } else {
                  this.modalMessage += (json && json.message) || this.errorMessage + ' ';
              }

          } catch (err) {
              // Network error -> treat as failure and ask user
              this.modalMessage = this.errorMessage + ' ';
          }
          previousVat = vat;
      }

      if (this.modalMessage) {
          this._pendingProceedCallback = typeof callback === 'function' ? callback : (form ? () => this._submitForm(form) : null);
          this.showModal = true;
          return await new Promise((resolve) => {
              this._resolveGuardPromise = resolve;
          });
      } else {
          if (typeof callback === 'function') {
              await callback();
          } else if (form) {
              this._submitForm(form);
          }
      }
    },
    _submitForm(form) {
      // Programmatically submit respecting original method/action
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    }
  }
};
</script>

<style scoped>
.vat-guard-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.vat-guard-modal {
  background: #fff;
  border-radius: 6px;
  max-width: 460px;
  width: calc(100% - 2rem);
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
}
.vat-guard-modal__body { padding: 16px 16px 0 16px; }
.vat-guard-modal__actions { padding: 12px 16px 16px 16px; text-align: right; }
.vat-guard-btn {
  appearance: none; border: 0; padding: 8px 14px; border-radius: 4px; cursor: pointer;
}
.vat-guard-btn + .vat-guard-btn { margin-left: 8px; }
</style>
